import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { RootState } from 'store';

import { flightsSearchParameters } from 'action-creators/flights';
import { confirmFlightSolicitation, fetchFlightDetails, validateFlight } from 'actions/flights';
import useFetching from 'hooks/useFetching';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { ConfirmMessage } from 'models/confirmMessage';
import { FlightResultResponse } from 'models/flightSearch';
import { FlightValidateResponse } from 'models/flightValidate';
import { AirValidationResultType, FlightValidationResponse } from 'models/flightValidation';
import FlightDetails from 'screens/components/FlightDetails';
import { amplitudelogEvent, ViewTravelItemDetailEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage } from 'utils/generic';

const INCOMPLETE_PROFILE_ERROR_MESSAGES = ["'Gender'", "'Phone'", "'DOB'"];

interface Props {
  show: boolean;
  flight?: FlightResultResponse;
  optionId?: number;
  upSellId?: string;
  upSellOptionId?: number;
  onClose: () => void;
  travelers?: TravelArrangerUsersAllowed[];
  index?: number;
  zIndex?: number;
}

const FlightDetailsContainer = ({
  show,
  optionId,
  onClose,
  upSellId,
  upSellOptionId = 0,
  travelers,
  index,
  zIndex,
}: Props) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<FlightValidationResponse | undefined>();
  const [showChurning, setShowChurning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmFlowMessage, setConfirmFlowMessage] = useState<ConfirmMessage>({});
  const [showConfirmFlow, setShowConfirmFlow] = useState(false);

  const { searchId, sessionToken, userId, fullState } = useSelector((state: RootState) => ({
    searchId: state.flights.search?.searchId,
    sessionToken: state.user.accountResult?.sessionToken,
    userId: state.user.accountResult?.id,
    fullState: state,
  }));

  const search = useSelector((state: RootState) => state.flights);
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);

  useFetching(() => {
    if (show) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    if (!optionId || !sessionToken || !searchId || !userId) return;

    const getDetails = async () => {
      try {
        setIsLoading(true);
        const {
          data: { data, succeeded },
        } = await fetchFlightDetails(
          searchId,
          sessionToken,
          optionId,
          travelers && travelers.length > 0 ? travelers[0].id : userId,
          search?.search?.distanceRestriction || 'international',
          search?.search?.lowestLogicalFare || 0,
          travelers && travelers.length > 0 ? travelers.length : 1,
          upSellId,
          upSellOptionId,
        );

        if (data.result === AirValidationResultType.PriceDifferent) {
          toast.add({ type: 'warning', message: t('flightDetails.error.priceChanged') });
        } else if (!succeeded) {
          Sentry.addBreadcrumb({
            category: 'flight',
            message: 'Flight validation solicitation not succeeded',
          });
          throw new Error();
        }

        setData({
          ...data,
          tripOption: { ...data.tripOption, optionId },
        });

        const amplitudeEvent: ViewTravelItemDetailEvent = {
          INDEX: index ?? 0,
          SOURCE: 'LIST',
          TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
          TRAVEL_FROM: search?.departurePoint?.code ?? '',
          TRAVEL_TO: search?.arrivalPoint?.code ?? '',
          TRAVEL_DESTINATION_TYPE: '',
          TRAVEL_TRIP_TYPE: '',
          TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), search?.departureDate).toString(),
          TRAVEL_DATE_FROM: search?.departureDate?.toString() ?? '',
          TRAVEL_DATE_TO: search?.returnDate?.toString() ?? '',
          TRAVEL_TRIP_LENGTH: (daysCount(search?.departureDate, search?.returnDate) + 1).toString(),
          TRAVEL_PASSENGERS: '1',
          TRAVEL_ADULTS: '1',
          TRAVEL_CHILDREN: '0',
          TRAVEL_INFANTS: '0',
          TRAVEL_CLASS_OF_SERVICE: '',
          TRAVEL_ROOMS: '1',
          TRAVEL_WITH_BAGGAGE: '',
          TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
          SEARCHID: search?.search?.searchId ?? '',
          TRAVEL_PRODUCT_CODE: data?.tripOption.sectors.first().flights.first().airlineCode ?? '',
          TRAVEL_PRODUCT_NAME: data?.tripOption.sectors.first().flights.first().airlineName ?? '',
          TRAVEL_SUB_PRODUCT: data?.tripOption.sectors.first().flights.first().fareFamilies ?? '',
          TRAVEL_PRODUCT_CATEGORY: data?.tripOption.sectors.first().flights.first().cabin ?? '',
          TRAVEL_PRODUCT_PRICE: '',
          TRAVEL_PRODUCT_PROVIDER: data?.tripOption.provider ?? '',
          TRAVEL_FARE_TYPE: '',
        };

        amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_ITEM_DETAIL, amplitudeEvent, userAccount);

        setIsLoading(false);
      } catch (ex) {
        const status = ex?.response?.status;

        let message = t('flightDetails.error.genericValidate');
        if (status === 400) {
          message = t('flightDetails.error.unavailable');
          handleFlightUnavailable();
          return;
        }

        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Flight validation solicitation error',
          data: { error: extractErrorMessage(ex), message },
        });

        setData(undefined);
        setIsLoading(false);

        toast.add({
          type: 'danger',
          message,
        });
        onClose();

        Sentry.captureMessage('[FlightDetails] Could not retrieve itinerary validation');
      }
    };

    if (optionId && show) getDetails();
  }, [optionId, show]);

  const handleConfirm = async (
    flight: FlightValidationResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
    card?:string
  ) => {
    setIsCreating(true);
    try {
      const responseValidate = await validateFlight(
        flight,
        justifyForBreakingRules,
        reason,
        fullState,
        upSellId,
        upSellOptionId,
        costCenterId,
        card
      );

      if (!responseValidate) {
        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Confirm flight solicitation error',
          data: { error: 'responseValidate is null or undefined' },
        });

        throw false;
      }

      if (!responseValidate.succeeded) {
        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Confirm flight solicitation error',
          data: { error: 'responseValidate.succeeded is false', response: responseValidate },
        });

        throw responseValidate;
      }

      const flightValidate: FlightValidateResponse = {
        documentId: responseValidate.data.documentId,
        validationId: responseValidate.data.validationId,
        validateResultType: '',
      };

      await dispatch<any>(
        confirmFlightSolicitation(
          flight,
          justifyForBreakingRules,
          reason,
          flightValidate,
          undefined,
          upSellId,
          upSellOptionId,
          costCenterId,
          breakRulesMessageId,
          card
        ),
      );

      setShowSuccess(true);
    } catch (ex) {
      const status = ex?.response?.status;
      const error = extractErrorMessage(ex);

      setIsCreating(false);

      let message = t('flightDetails.error.generic');
      if (INCOMPLETE_PROFILE_ERROR_MESSAGES.some((msg) => error?.startsWith(msg))) {
        message = t('flightDetails.error.incompleteProfile');
      } else if (error?.includes('Churning')) {
        setShowChurning(true);
        message = '';
      } else if (error?.indexOf('travel arranger is allowed') !== -1) {
        toast.add({
          type: 'danger',
          message: t('flightDetails.error.userDoesNotBelongToTheTravelArranger'),
        });
        return;
      } else if (error?.indexOf('Debes pertenecer a un grupo') !== -1) {
        toast.add({
          type: 'danger',
          message: t('flightDetails.error.userMustBeAssignedToGroup'),
        });
        return;
      } else if (status === 400) {
        message = t('flightDetails.error.unavailable');
        handleFlightUnavailable(error);
        return;
      }

      Sentry.addBreadcrumb({
        category: 'flight',
        message: 'Confirm flight solicitation error',
        data: { error, message },
      });

      if (message)
        toast.add({
          type: 'danger',
          message,
        });

      Sentry.captureMessage('[FlightDetails] Could not create travel');
    }
  };

  const handleFlightUnavailable = (error?: string) => {
    if (error?.indexOf('Duplex') > -1) {
      setConfirmFlowMessage({
        text: t('flightDetails.error.duplex.text'),
        button: t('flightDetails.error.duplex.button'),
        title: t('flightDetails.error.duplex.title'),
      });
    } else {
      setConfirmFlowMessage({
        text: t('flightDetails.error.unavailable.text'),
        button: t('flightDetails.error.unavailable.button'),
        title: t('flightDetails.error.unavailable.title'),
      });
    }

    setShowConfirmFlow(true);
  };

  const reloadSearch = async () => {
    if (travelers === undefined || travelers?.length === 0) {
      const currentUserAsTraveler: TravelArrangerUsersAllowed = {
        id: userAccount.id,
        userId: userAccount.id,
        name: userAccount.name,
        lastName: userAccount.lastName,
        email: userAccount.email,
        phoneNumber: userAccount.phoneNumber,
        userGroupName: userAccount.userGroupName,
        userGroupId: userAccount.userGroupId,
        favorite: true, // TODO fijar ese campo
        guest: userAccount.isGuestBookAllowed, // TODO fijar ese campo
        birthDate: userAccount.birthDate.toString(),
        country: userAccount.country,
        documentNumber: userAccount.documentNumber,
        gender: userAccount.gender,
        passportNumber: userAccount.passportNumber,
      };

      travelers.push(currentUserAsTraveler);
    }

    await dispatch(
      flightsSearchParameters({
        baggageIncluded: fullState.flights.baggageIncluded,
        departurePoint: fullState.flights.departurePoint,
        departureDate: fullState.flights.departureDate,
        arrivalPoint: fullState.flights.arrivalPoint,
        cabin: fullState.flights.cabin,
        returnDate: fullState.flights.returnDate,
        travelers,
        departureTime: fullState.flights.departureTime,
        returnTime: fullState.flights.returnTime,
        directFlight: fullState.flights.directFlight,
        multiDestinationTrips: search?.multiDestinationTrips,
        flightStretch: search?.flightStretch,
      }),
    );

    setIsLoading(false);
    setShowConfirmFlow(false);
    setIsCreating(false);
    history.replace('/corporate/flights');
  };

  const handleConfirmFlow = async () => {
    reloadSearch();
  };

  const handleConfirmFlowClose = () => {
    reloadSearch();
  };

  return (
    <FlightDetails
      show={show}
      creating={isCreating}
      loading={isLoading}
      details={data}
      onClose={onClose}
      onConfirm={handleConfirm}
      showChurning={showChurning}
      setShowChurning={setShowChurning}
      travelers={travelers}
      onConfirmFlow={handleConfirmFlow}
      onConfirmFlowClose={handleConfirmFlowClose}
      confirmFlowMessage={confirmFlowMessage}
      showConfirmFlow={showConfirmFlow}
      zIndex={zIndex}
    />
  );
};

export default FlightDetailsContainer;
